import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { GenderService } from '../../services/gender.service';
import { GradeService } from '../../services/grade.service';
import { RegistrationService } from '../../services/registration.service';
import { AccountService } from '../../services/account.service';
import { GenderDto, GradeDto } from '../../models';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
})
export class RegisterComponent implements OnInit {
  step = 1;                  // 1 = role selection, 2 = fill form
  selectedRole = '';
  isLoading = false;
  isGendersLoading = true;
  isGradesLoading = true;
  isTenantLoading = true;
  tenantName = '';

  genders: GenderDto[] = [];
  grades: GradeDto[] = [];

  roleForm: FormGroup;
  registerForm: FormGroup;

  readonly roles = [
    { value: 'Student',  labelEn: 'Student',  labelAr: 'طالب',     icon: 'bi-mortarboard' },
    { value: 'Teacher',  labelEn: 'Teacher',  labelAr: 'معلم',     icon: 'bi-person-workspace' },
    { value: 'Parent',   labelEn: 'Parent',   labelAr: 'ولي أمر',  icon: 'bi-people' },
  ];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private toastr: ToastrService,
    private genderSrv: GenderService,
    private gradeSrv: GradeService,
    private registrationSvc: RegistrationService,
    private accountSrv: AccountService,
  ) {
    this.roleForm = this.fb.group({ role: ['', Validators.required] });

    this.registerForm = this.fb.group(
      {
        fullNameAr:         ['', [Validators.required, Validators.minLength(3)]],
        fullNameEn:         ['', [Validators.required, Validators.minLength(3)]],
        email:              ['', [Validators.required, Validators.email]],
        phoneNumber:        ['', [Validators.required, Validators.pattern('^[0-9+]{8,15}$')]],
        dateOfBirth:        ['', Validators.required],
        genderId:           ['', Validators.required],
        userName:           ['', [Validators.required, Validators.minLength(3)]],
        password:           ['', [Validators.required, Validators.minLength(8)]],
        confirmPassword:    ['', Validators.required],
        // Student-only
        gradeId:            [''],
        parentPhoneNumber:  [''],
      },
      { validators: this.passwordMatchValidator }
    );
  }

  ngOnInit(): void {
    this.loadTenant();
    this.loadGenders();
    this.loadGrades();
  }

  get tenantId(): string {
    return localStorage.getItem('tenantId') || '';
  }

  selectRole(role: string): void {
    this.selectedRole = role;
    this.roleForm.patchValue({ role });

    // Apply/remove validators based on role
    const gradeCtrl = this.registerForm.get('gradeId');
    const parentCtrl = this.registerForm.get('parentPhoneNumber');

    if (role === 'Student') {
      gradeCtrl?.setValidators([Validators.required]);
      parentCtrl?.setValidators([Validators.required, Validators.pattern('^[0-9+]{8,15}$')]);
    } else {
      gradeCtrl?.clearValidators();
      parentCtrl?.clearValidators();
    }
    gradeCtrl?.updateValueAndValidity();
    parentCtrl?.updateValueAndValidity();

    this.step = 2;
  }

  backToRoleStep(): void {
    this.step = 1;
    this.selectedRole = '';
  }

  onSubmit(): void {
    if (this.registerForm.invalid) {
      this.registerForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    const v = this.registerForm.value;

    const dto = {
      tenantId: this.tenantId,
      role: this.selectedRole,
      userName: v.userName,
      email: v.email,
      password: v.password,
      fullNameAr: v.fullNameAr,
      fullNameEn: v.fullNameEn,
      phoneNumber: v.phoneNumber,
      genderId: v.genderId ? +v.genderId : undefined,
      dateOfBirth: v.dateOfBirth,
      gradeId: this.selectedRole === 'Student' ? v.gradeId : undefined,
      parentPhoneNumber: this.selectedRole === 'Student' ? v.parentPhoneNumber : undefined,
    };

    this.registrationSvc.selfRegister(dto).subscribe({
      next: (res: any) => {
        const success = res?.value ?? res?.isSuccess;
        if (success) {
          this.toastr.success(
            'Registration submitted! Enter the verification code we sent to your email/WhatsApp.',
            '',
            { timeOut: 6000 }
          );
          this.router.navigate(['/account/verify'], { queryParams: { email: v.email } });
        } else {
          const msg = res?.errors?.[0]?.messageEn || 'Registration failed.';
          this.toastr.error(msg);
        }
        this.isLoading = false;
      },
      error: (err: any) => {
        const msg = err?.error?.errors?.[0]?.messageEn || 'Registration failed. Please try again.';
        this.toastr.error(msg);
        this.isLoading = false;
      },
    });
  }

  private loadTenant(): void {
    this.accountSrv.getTenentIdByURL().subscribe({
      next: (res) => {
        if (res.isSuccess && res.value) {
          localStorage.setItem('tenantId', res.value.id!);
          this.tenantName = res.value.nameEn || res.value.nameAr || '';
        }
        this.isTenantLoading = false;
      },
      error: () => { this.isTenantLoading = false; },
    });
  }

  private loadGenders(): void {
    this.genderSrv.getGenderList().subscribe({
      next: (res: any) => {
        const all = Array.isArray(res) ? res : (res?.value ?? []);
        this.genders = all.filter((g: any) => g.id !== 3); // exclude 'Other'
        this.isGendersLoading = false;
      },
      error: () => { this.isGendersLoading = false; },
    });
  }

  private loadGrades(): void {
    this.gradeSrv.getGradeList().subscribe({
      next: (res: any) => {
        this.grades = Array.isArray(res) ? res : (res?.value ?? []);
        this.isGradesLoading = false;
      },
      error: () => { this.isGradesLoading = false; },
    });
  }

  private passwordMatchValidator(group: AbstractControl): ValidationErrors | null {
    const pass = group.get('password')?.value;
    const confirm = group.get('confirmPassword')?.value;
    return pass === confirm ? null : { passwordMismatch: true };
  }

  fieldInvalid(name: string): boolean {
    const ctrl = this.registerForm.get(name);
    return !!(ctrl?.invalid && ctrl?.touched);
  }
}
